import { adoptUserId } from '../auth/auth';
import type { SqlDriver } from '../data/db/driver';
import type { Store } from '../data/store/store';
import type { SyncTransport } from './types';

export interface LinkResult {
  /** The user id this person has on every device from now on. */
  userId: string;
  /** This device joined an account that already existed (it adopted the account's id). */
  joined: boolean;
  /** Local budgets that were still empty and were left behind instead of synced. */
  droppedEmpty: string[];
}

/**
 * Right after signing in: tie this install to the account.
 *
 * First device of an account: its local user id becomes the account's, and
 * everything it has is pushed as is. A later device adopts that id: its
 * own budgets are handed over to it (so they sync as this person's), and a
 * budget that is still empty (the one onboarding creates before you had a
 * chance to sign in) is left out, so the account does not grow a second,
 * empty "Personal" on every new phone.
 */
export async function linkAccount(db: SqlDriver, store: Store, transport: SyncTransport, localUserId: string, displayName: string): Promise<LinkResult> {
  const userId = await transport.claim(localUserId);
  if (userId === localUserId) return { userId, joined: false, droppedEmpty: [] };

  const droppedEmpty: string[] = [];
  await store.users.ensureLocal(userId, displayName);
  for (const space of await store.spaces.listForUser(localUserId)) {
    const [{ n }] = (await db.all<{ n: number }>(
      `SELECT (SELECT COUNT(*) FROM transactions WHERE space_id = ?) + (SELECT COUNT(*) FROM recurring_templates WHERE space_id = ?) AS n`,
      [space.id, space.id],
    )) as [{ n: number }];
    if (Number(n) === 0) {
      // Never reached the server: drop its pending writes and hide it here.
      await db.batch([
        { sql: 'DELETE FROM sync_outbox WHERE space_id = ? OR (table_name = ? AND row_id = ?)', params: [space.id, 'budget_spaces', space.id] },
        { sql: 'UPDATE budget_spaces SET deleted_at = ? WHERE id = ?', params: [Date.now(), space.id] },
      ]);
      droppedEmpty.push(space.id);
      continue;
    }
    if (space.ownerUserId === localUserId) await store.spaces.update(space.id, { ownerUserId: userId });
  }
  for (const m of await store.members.list({ userId: localUserId })) {
    if (!droppedEmpty.includes(m.spaceId)) await store.members.update(m.id, { userId });
  }
  await adoptUserId(db, userId);
  return { userId, joined: true, droppedEmpty };
}
